import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Chatbot.css';

// Mock product objects matching Products.jsx to enable direct navigation from the chatbot
const chatProducts = {
  parkgenie: {
    id: 1,
    name: 'ParkGenie - Parking Management System',
    category: 'Digital Parking Management System (PMS)',
    tagline: 'Next-Generation Digital Parking Management System (PMS)',
    price: 'Starting from ₹4,99,999',
    color: '#2563eb',
    version: 'v2.5',
    rating: 4.8,
    reviews: 542,
    badges: ['Zero Hardware', 'Paperless Security', 'Real-time Queries'],
    stats: [
      { value: 'Zero', label: 'Hardware Required' },
      { value: '3', label: 'Vehicle Categories' },
      { value: '100%', label: 'Paperless Security' },
      { value: 'Real-time', label: 'Slot Allocation' }
    ],
    features: [
      { icon: 'bi-clipboard-check', title: 'Smart Registration & Check-In', desc: 'Instantly register vehicles for hourly, daily, or monthly rental packages.' },
      { icon: 'bi-grid-3x3-gap-fill', title: 'Intelligent Slot Allocation', desc: 'Dedicated slots optimized for Two-Wheelers, Light Motor Vehicles, and Heavy Motor Vehicles.' }
    ],
    plans: [
      { name: 'Basic', price: '₹4,99,999', period: 'One-time', desc: 'For small parking lots (up to 50 spaces).', features: ['Up to 50 Parking Spaces', 'All Core Features Included'] },
      { name: 'Professional', price: '₹9,99,999', period: 'One-time', desc: 'For medium parking facilities (50-200 spaces).', features: ['Up to 200 Parking Spaces', 'All Core Features Included'] },
      { name: 'Enterprise', price: 'Custom', period: 'pricing', desc: 'For large multi-location operations.', features: ['Unlimited Parking Spaces', 'All Core Features Included'] }
    ]
  },
  finvecto: {
    id: 2,
    name: 'Finvecto - Accounting Software for Small Business',
    category: 'Accounting & Finance',
    tagline: 'Precision in Account. Clarity in Growth.',
    price: 'Starting from ₹1,50,000',
    color: '#10b981',
    version: 'v3.1',
    rating: 4.9,
    reviews: 891,
    badges: ['GST Compliant', 'Ledger Focus', 'PDF Reporting'],
    stats: [
      { value: '2,000+', label: 'Active Businesses' },
      { value: '100%', label: 'Ledger Accuracy' }
    ],
    features: [
      { icon: 'bi-cash-coin', title: 'Accounts Receivable & Sales', desc: 'Manage customer profiles, sales invoices, and ledger balances.' }
    ],
    plans: [
      { name: 'Standard License', price: '₹1,50,000', period: 'One-time', desc: 'Complete full-featured accounting software.', features: ['All Core Features Included'] }
    ]
  }
};

const Chatbot = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [unread, setUnread] = useState(false);
  const messagesEndRef = useRef(null);

  // Initialize chatbot messages
  useEffect(() => {
    const savedChat = sessionStorage.getItem('lkk_chat_history');
    if (savedChat) {
      setMessages(JSON.parse(savedChat));
    } else {
      const initialMessages = [
        {
          id: 1,
          sender: 'bot',
          text: 'Hi! Welcome to LKK Tech Solutions. I am your digital assistant. How can I help you today?',
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          quickReplies: ['ParkGenie System', 'Finvecto Accounting', 'Careers', 'Contact Sales']
        }
      ];
      setMessages(initialMessages);
      setUnread(true);
    }
  }, []);

  // Persist chat history
  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem('lkk_chat_history', JSON.stringify(messages));
    }
  }, [messages]);

  // Scroll to bottom of message logs
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setUnread(false);
    }
  };

  const handleSend = (text) => {
    if (!text.trim()) return;

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const userMessage = {
      id: messages.length + 1,
      sender: 'user',
      text: text,
      time: timestamp
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);

    // Simulate bot response loading delay
    setTimeout(() => {
      const response = generateBotResponse(text);
      const botMessage = {
        id: messages.length + 2,
        sender: 'bot',
        text: response.text,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        quickReplies: response.quickReplies || []
      };
      setMessages((prev) => [...prev, botMessage]);
      setIsTyping(false);
    }, 1000);
  };

  const generateBotResponse = (userText) => {
    const cleanText = userText.toLowerCase().trim();

    // Specific Navigation Quick Replies
    if (cleanText === 'go to careers') {
      navigate('/careers');
      return {
        text: 'I have redirected you to our Careers page where you can see all current job openings and apply. Is there anything else you need?',
        quickReplies: ['Products', 'Contact Sales', 'Go back']
      };
    }
    
    if (cleanText === 'go to contact page' || cleanText === 'go to contact') {
      navigate('/contact');
      return {
        text: 'I have redirected you to our Contact page. You can fill out the enquiry form or check our contact numbers. How else can I help?',
        quickReplies: ['Products', 'Careers', 'Go back']
      };
    }

    if (cleanText === 'watch finvecto demo' || cleanText === 'view finvecto details') {
      navigate('/product', { state: { product: chatProducts.finvecto } });
      return {
        text: 'I have loaded the product detail page for Finvecto. You can click on the "Watch Demo" button directly on that page to view the demo video!',
        quickReplies: ['Finvecto Pricing', 'ParkGenie Details', 'Go back']
      };
    }

    if (cleanText === 'view parkgenie details') {
      navigate('/product', { state: { product: chatProducts.parkgenie } });
      return {
        text: 'I have loaded the product detail page for ParkGenie for you. Let me know if you would like to ask questions about its plans or features.',
        quickReplies: ['ParkGenie Pricing', 'Finvecto Details', 'Go back']
      };
    }

    // Rules engine
    if (cleanText.includes('parkgenie') || cleanText.includes('parking') || cleanText.includes('pms')) {
      if (cleanText.includes('pricing') || cleanText.includes('price') || cleanText.includes('cost')) {
        return {
          text: 'PARKGENIE standard licensing starts from ₹4,99,999. It features automated billing, zero hardware installation, intelligent slot allocation for all vehicle types, and secure web dashboards. Would you like to check details?',
          quickReplies: ['View ParkGenie Details', 'Contact Sales', 'Go back']
        };
      }
      return {
        text: 'PARKGENIE is LKK Tech’s next-generation digital Parking Management System (PMS). It supports hourly check-ins, daily rentals, and monthly subscriptions with zero external hardware required.',
        quickReplies: ['ParkGenie Pricing', 'View ParkGenie Details', 'Go back']
      };
    }

    if (cleanText.includes('finvecto') || cleanText.includes('accounting') || cleanText.includes('ledger') || cleanText.includes('gst')) {
      if (cleanText.includes('pricing') || cleanText.includes('price') || cleanText.includes('cost')) {
        return {
          text: 'Finvecto standard license starts at ₹1,50,000 (one-time purchase). It supports up to 4 concurrent roles (Admin, Sales Manager, Purchase Manager, and Standard User). Unlimited transactions and GST logs included.',
          quickReplies: ['Watch Finvecto Demo', 'Contact Sales', 'Go back']
        };
      }
      return {
        text: 'Finvecto handles Ledger Management, Accounts Receivable, and Accounts Payable with extreme speed. It has integrated GST compliance, custom PDF invoice generators, and audit logging built-in.',
        quickReplies: ['Finvecto Pricing', 'Watch Finvecto Demo', 'Go back']
      };
    }

    if (cleanText.includes('career') || cleanText.includes('job') || cleanText.includes('join') || cleanText.includes('hiring') || cleanText.includes('vacancy')) {
      return {
        text: 'We are hiring software engineers, business consultants, and support technicians. Check out our Careers page to see vacancies and submit your application.',
        quickReplies: ['Go to Careers', 'Contact Us', 'Go back']
      };
    }

    if (cleanText.includes('pricing') || cleanText.includes('price') || cleanText.includes('cost') || cleanText.includes('how much')) {
      return {
        text: 'LKK Tech Solutions products list:\n\n1. Finvecto: Starting from ₹1,50,000 (standard multi-user license)\n2. ParkGenie: Starting from ₹4,99,999 (complete infrastructure mapping)\n\nContact our sales team for custom integrations and discounts!',
        quickReplies: ['Finvecto Pricing', 'ParkGenie Pricing', 'Contact Sales']
      };
    }

    if (cleanText.includes('contact') || cleanText.includes('sales') || cleanText.includes('support') || cleanText.includes('email') || cleanText.includes('phone') || cleanText.includes('meet') || cleanText.includes('call')) {
      return {
        text: 'You can reach us at contact@lkktechsolutions.in or fill out our enquiry form. You can also click "Meet Now" at the bottom-left to book a Calendly call directly.',
        quickReplies: ['Go to Contact Page', 'Go back']
      };
    }

    if (cleanText.includes('hello') || cleanText.includes('hi') || cleanText.includes('hey') || cleanText.includes('greetings')) {
      return {
        text: 'Hi there! How can I assist you with our products and services today?',
        quickReplies: ['Finvecto Accounting', 'ParkGenie System', 'Careers', 'Contact Sales']
      };
    }

    if (cleanText === 'go back' || cleanText === 'back') {
      return {
        text: 'Sure, let\'s start over. How can LKK Tech Solutions assist you today?',
        quickReplies: ['ParkGenie System', 'Finvecto Accounting', 'Careers', 'Contact Sales']
      };
    }

    // Default Fallback
    return {
      text: 'I\'m sorry, I didn\'t catch that query. You can ask me about Finvecto Accounting, ParkGenie Parking, pricing, contact details, or careers. Or use one of the quick replies below:',
      quickReplies: ['ParkGenie System', 'Finvecto Accounting', 'Careers', 'Contact Sales']
    };
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend(inputText);
    }
  };

  return (
    <>
      {/* Floating Trigger Button */}
      <button 
        className={`chatbot-trigger ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        aria-label="Open support chat"
      >
        {unread && !isOpen && <span className="chatbot-badge" />}
        {isOpen ? (
          <i className="bi bi-x-lg" />
        ) : (
          <i className="bi bi-chat-dots-fill" />
        )}
      </button>

      {/* Chat window */}
      <div className={`chatbot-window ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-header-info">
            <div className="chatbot-avatar">
              <i className="bi bi-robot" />
            </div>
            <div>
              <h4 className="chatbot-title">LKK Tech Assistant</h4>
              <div className="chatbot-status">
                <span className="chatbot-status-dot" />
                <span>Online</span>
              </div>
            </div>
          </div>
          <button 
            className="chatbot-close-btn" 
            onClick={() => setIsOpen(false)}
            aria-label="Close chat"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* Message Log */}
        <div className="chatbot-body">
          {messages.map((msg) => (
            <div key={msg.id} className={`chatbot-message ${msg.sender}`}>
              <div className="chatbot-bubble">
                {msg.text.split('\n').map((line, idx) => (
                  <span key={idx}>
                    {line}
                    {idx < msg.text.split('\n').length - 1 && <br />}
                  </span>
                ))}
              </div>
              <span className="chatbot-time">{msg.time}</span>
              
              {/* Render quick replies if present and it's the latest bot message */}
              {msg.sender === 'bot' && 
               msg.quickReplies && 
               msg.quickReplies.length > 0 && 
               messages[messages.length - 1]?.id === msg.id && (
                <div className="chatbot-quick-replies-container">
                  {msg.quickReplies.map((reply, index) => (
                    <button
                      key={index}
                      className="chatbot-quick-reply-btn"
                      onClick={() => handleSend(reply)}
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="chatbot-message bot">
              <div className="chatbot-bubble">
                <div className="chatbot-typing-dots">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="chatbot-input-panel">
          <div className="chatbot-input-wrapper">
            <input
              type="text"
              placeholder="Type your message here..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isTyping}
            />
          </div>
          <button
            className="chatbot-send-btn"
            onClick={() => handleSend(inputText)}
            disabled={!inputText.trim() || isTyping}
            aria-label="Send message"
          >
            <i className="bi bi-send-fill" />
          </button>
        </div>
      </div>
    </>
  );
};

export default Chatbot;
